/**
 * tamAck.js — Encrypted TAM (tamper) acknowledgement
 *
 * When an admin presses "Acknowledge" on a tamper alert (toast,
 * device tamper-history sheet, or the Miscellaneous page), we publish:
 *
 *   topic   : /hoags/livpure/lotier/waas/<macid>/   (device command topic)
 *   payload : { IM:<macid>, CMD:"TAM", ACK:"T" }   (AES-256-CBC encrypted)
 *
 * Encryption uses the SAME K2 + flow as the Device Status (DS) request:
 *   1. Use a cached K2 if we already have one (getK2 / window.__hoagsK2)
 *   2. Otherwise do a fresh handshake → derive K1 → decrypt K2 (like DS)
 *   3. aesEncrypt(stringToHex(JSON)) with K2 → publish RAW encrypted hex
 *
 * Falls back to plaintext only if K2 truly cannot be obtained.
 */
import { generateK1, aesDecrypt, aesEncrypt, stringToHex } from './cryptoUtils';

const API_BASE = ''; // served from same origin as the proxy (relative URL)

// Sanitise MAC — must match DeviceStatus.js sanitiseMac():
// API returns 13-char '9900996DDD30' but the device expects 12-char.
// Rule: if mac is 13 hex chars, drop the duplicate char at position 7.
function sanitiseMac(mac) {
  const m = (mac || '').replace(/[^a-fA-F0-9]/g, '');
  if (m.length === 13) return (m.slice(0, 7) + m.slice(8)).toLowerCase();
  return m.toLowerCase();
}

const TAM_ACK_TOPIC = (macSan) =>
  `/hoags/livpure/lotier/waas/${macSan}/`;

/**
 * Send an encrypted TAM acknowledgement to the device.
 *
 * @param {string}   rawMac   device.macid as stored (may be 13-char)
 * @param {function} publish  publish(topic, message) from useMqttProxy()
 * @param {function} [getK2]  optional getK2(macId) from useDeviceKeys()
 */
export async function sendTamAck(rawMac, publish, getK2) {
  const macSan   = sanitiseMac(rawMac);                                   // topic + IM (matches DS / device)
  const macUpper = (rawMac || '').replace(/[^a-fA-F0-9]/g, '').toUpperCase(); // K1 / handshake
  const macKey   = (rawMac || '').replace(/[^a-fA-F0-9]/g, '').toLowerCase(); // cache key

  const topic    = TAM_ACK_TOPIC(macSan);
  const payload  = { IM: macSan, CMD: 'TAM', ACK: 'T' };
  const plainStr = JSON.stringify(payload);

  console.log(`[TAM ACK] 📝 Plain JSON (before encrypt): ${plainStr}  → ${topic}`);

  // 1. Try a cached K2 (DeviceStatus / DeviceList handshakes populate these)
  let k2 =
    (getK2 ? getK2(rawMac) : null) ||
    window.__hoagsK2?.[macKey] ||
    window.__hoagsK2?.[macSan] ||
    null;

  // 2. No K2 yet → do a fresh handshake (identical to the DS flow)
  if (!k2) {
    try {
      const k1 = await generateK1(macUpper);
      const res = await fetch(`${API_BASE}/api/handshake`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          macId: macUpper,
          payload: { IM: macUpper, KY: '', CMD: 'HDS' },
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.KY) {
          k2 = await aesDecrypt(data.KY, k1);
          window.__hoagsK2 = window.__hoagsK2 || {};
          window.__hoagsK2[macKey] = k2; // cache for next time
          console.log(`[TAM ACK] 🤝 K2 via handshake for ${macUpper}`);
        }
      }
    } catch (e) {
      console.error('[TAM ACK] handshake failed:', e.message);
    }
  }

  // 3. Encrypt + publish RAW encrypted hex — identical to the DS request (no VAL wrapper)
  try {
    if (k2) {
      const encrypted = await aesEncrypt(stringToHex(plainStr), k2);
      publish(topic, encrypted);
      console.log(`[TAM ACK] ✅ encrypted ACK (raw hex) → ${topic}  ${encrypted.substring(0, 40)}… (${encrypted.length} chars)`);
    } else {
      publish(topic, plainStr);
      console.warn(`[TAM ACK] ⚠️ plaintext ACK (no K2) → ${topic}`);
    }
  } catch (e) {
    console.error('[TAM ACK] encrypt failed, sending plaintext:', e.message);
    publish(topic, plainStr);
  }
}

export default sendTamAck;
